import { Core } from '@pdftron/webviewer';
import { WebViewerWrapper } from 'pdftron';
import { AnnotationCustomData, DocumentTypes } from 'types';
import GVAnnotationMixin from './GVAnnotationMixin';
import GraphicZone from '../GraphicZone';
import MarkupZone from '../MarkupZone';
import store from 'store/store';
import { getTransparencyOpacity } from 'store';

// Per-side padding so the annotation's invalidation region covers the label area.
const ANIMATION_RECT_LABEL_PADDING = 100;

export interface AnimationConfig {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  sourceImageBitmap: ImageBitmap;
  targetImageBitmap: ImageBitmap | null; // null in transparentOverlay mode
  animationId: string; // unique ID to track if animation should continue
  intervalName: 'graphicAnimationInterval' | 'markupAnimationInterval';
  annotationSubject: 'GraphicAnnotationAnimated' | 'MarkupAnnotationAnimated' | 'GraphicAnnotationOverlay';
  drawFn: (
    ctx: CanvasRenderingContext2D,
    imageBitmap: ImageBitmap,
    details: { x: number; y: number; width: number; height: number },
    isSource: boolean,
    alpha?: number,
  ) => void;
  onBeforeAnimate?: () => void; // Optional callback before animation starts (for hiding repeats, etc.)
  graphicMode?: 'flashing' | 'transparentOverlay'; // Optional: determines animation type
}

export class FlashAnimationManager {
  private static rotationListenerCleanups: Map<string, () => void> = new Map();

  /**
   * Helper to fetch image blobs from URLs
   */
  static async fetchImageBlob(imageUrl: string): Promise<Blob> {
    const response = await fetch(imageUrl, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    return response.blob();
  }

  /**
   * Create and start a flashing animation between source and target images
   */
  static async startFlashAnimation(
    targetWrapper: WebViewerWrapper,
    config: AnimationConfig,
    getCurrentAnimationId: () => string | null, // function to check if animation should still run
  ): Promise<void> {
    const {
      pageNumber,
      x,
      y,
      width,
      height,
      sourceImageBitmap,
      targetImageBitmap,
      animationId,
      intervalName,
      annotationSubject,
      drawFn,
      onBeforeAnimate,
      graphicMode = 'flashing', // Default to flash mode if not specified
    } = config;

    const annotationDetails = { x, y, width, height };
    const isOverlayMode = graphicMode === 'transparentOverlay';

    // Determine the subject based on mode
    const finalSubject = isOverlayMode ? 'GraphicAnnotationOverlay' : annotationSubject;

    // Expanded rect so the label (drawn outside x,y,w,h) is inside the invalidation region.
    const { Math: PdfMath } = targetWrapper.instance.Core;
    const expandedRect = new PdfMath.Rect(
      x - ANIMATION_RECT_LABEL_PADDING,
      y - ANIMATION_RECT_LABEL_PADDING,
      x + width + ANIMATION_RECT_LABEL_PADDING,
      y + height + ANIMATION_RECT_LABEL_PADDING,
    );

    // Create source animation annotation
    const sourceAnimationAnnotation = new (GVAnnotationMixin(
      targetWrapper.instance.Core.Annotations.RectangleAnnotation,
    ))();
    sourceAnimationAnnotation.ReadOnly = true;
    // Unselectable so a miss (click on the flashing image, not a difference box) does not
    // steal selection. Clicks on difference boxes are handled by raising those annotations
    // above this overlay — Listable=false does not pass mouse events through.
    sourceAnimationAnnotation.Listable = false;
    sourceAnimationAnnotation.Subject = finalSubject;
    sourceAnimationAnnotation.PageNumber = pageNumber;
    sourceAnimationAnnotation.setRect(expandedRect);
    sourceAnimationAnnotation.draw = (ctx: CanvasRenderingContext2D) => {
      const opacity = isOverlayMode ? getTransparencyOpacity(store.getState()) : 1;
      drawFn(ctx, sourceImageBitmap, annotationDetails, true, opacity);
    };

    // Prepare annotations collection
    const newAnnotations: Core.Annotations.Annotation[] = [sourceAnimationAnnotation];

    // Create target annotation only for flashing mode
    let targetAnimationAnnotation: any = null;
    if (!isOverlayMode) {
      // Ensure we have a target bitmap for flashing mode
      if (!targetImageBitmap) {
        console.error('FlashAnimationManager: targetImageBitmap is required for flashing mode');
        return;
      }

      targetAnimationAnnotation = new (GVAnnotationMixin(
        targetWrapper.instance.Core.Annotations.RectangleAnnotation,
      ))();
      targetAnimationAnnotation.ReadOnly = true;
      targetAnimationAnnotation.Listable = false;
      targetAnimationAnnotation.Subject = finalSubject;
      targetAnimationAnnotation.PageNumber = pageNumber;
      targetAnimationAnnotation.setRect(expandedRect);
      targetAnimationAnnotation.draw = (ctx: CanvasRenderingContext2D) =>
        drawFn(ctx, targetImageBitmap, annotationDetails, false);
      newAnnotations.push(targetAnimationAnnotation);

      // Start with target visible, source hidden
      targetAnimationAnnotation.Hidden = false;
      sourceAnimationAnnotation.Hidden = true;
    } else {
      // In overlay mode, just show the source annotation with transparency
      sourceAnimationAnnotation.Hidden = false;
    }

    // Add annotations to the target document
    targetWrapper.annotationManager.addAnnotation(newAnnotations);

    FlashAnimationManager.bringAnimationAnnotationsToFront(targetWrapper, newAnnotations);

    // Wait for annotations to be drawn, then start the animation loop (or just show overlay)
    await targetWrapper.annotationManager.drawAnnotations({ pageNumber });

    // Check if animation should still run (user might have switched differences).
    // Must precede the rotationUpdated listener so a discarded animation can't leak it.
    if (getCurrentAnimationId() !== animationId) return;

    // Force an immediate per-page redraw on rotation; otherwise stale label pixels remain.
    const { documentViewer } = targetWrapper.instance.Core;
    const rotationHandler = (_rotation: unknown, rotatedPageNumber?: number) => {
      if (rotatedPageNumber !== undefined && rotatedPageNumber !== pageNumber) return;
      targetWrapper.annotationManager.drawAnnotations({ pageNumber });
    };
    documentViewer.addEventListener('rotationUpdated', rotationHandler);
    const prevCleanup = FlashAnimationManager.rotationListenerCleanups.get(finalSubject);
    if (prevCleanup) prevCleanup();
    FlashAnimationManager.rotationListenerCleanups.set(finalSubject, () => {
      documentViewer.removeEventListener('rotationUpdated', rotationHandler);
    });

    // Execute any pre-animation callback (e.g., hide repeat annotations for graphics)
    if (onBeforeAnimate) {
      onBeforeAnimate();
    }

    // Start the flashing interval only if not in overlay mode
    if (!isOverlayMode) {
      window[intervalName] = window.requestInterval(() => {
        window.currentShownDocument = !sourceAnimationAnnotation.Hidden ? DocumentTypes.target : DocumentTypes.source;
        targetAnimationAnnotation.Hidden = !targetAnimationAnnotation.Hidden;
        sourceAnimationAnnotation.Hidden = !sourceAnimationAnnotation.Hidden;
        targetWrapper.annotationManager.updateAnnotation(targetAnimationAnnotation);
        targetWrapper.annotationManager.updateAnnotation(sourceAnimationAnnotation);
        FlashAnimationManager.bringAnimationAnnotationsToFront(targetWrapper, newAnnotations);
        // Full-page redraw each tick so the previous frame's label pixels are cleared.
        targetWrapper.annotationManager.drawAnnotations({ pageNumber });
      }, 500);
    }
  }

  /**
   * Raises the flash overlay, then difference boxes on the same page.
   * Animation first so Source/New labels stay above the dim overlay; differences last so they
   * remain clickable inside the match (the overlay rect covers a whole repeat).
   */
  private static bringAnimationAnnotationsToFront(
    targetWrapper: WebViewerWrapper,
    annotations: Core.Annotations.Annotation[],
  ): void {
    const annotationManager = targetWrapper.annotationManager;
    annotations.forEach((annot) => {
      // Guard against annotations removed between frames (e.g. animation switched).
      if (annotationManager.getAnnotationById(annot.Id)) {
        annotationManager.bringToFront(annot);
      }
    });

    const pageNumber = annotations[0]?.PageNumber;
    annotationManager
      .getAnnotationsList()
      .filter(
        (annot) =>
          annot.getCustomData(AnnotationCustomData.difference) === 'true' &&
          (pageNumber === undefined || annot.PageNumber === pageNumber),
      )
      .forEach((annot) => {
        if (annotationManager.getAnnotationById(annot.Id)) {
          annotationManager.bringToFront(annot);
        }
      });
  }

  /**
   * Stop an animation and clean up annotations
   */
  static stopAnimation(
    intervalName: 'graphicAnimationInterval' | 'markupAnimationInterval',
    annotationSubject: string,
    sourceWrapper: WebViewerWrapper | null,
    targetWrapper: WebViewerWrapper | null,
  ): void {
    // Clear the interval
    if (window[intervalName]) {
      window.clearRequestInterval(window[intervalName]);
      window[intervalName] = null;
    }

    // Remove the rotationUpdated listener registered in startFlashAnimation
    const rotationCleanup = FlashAnimationManager.rotationListenerCleanups.get(annotationSubject);
    if (rotationCleanup) {
      rotationCleanup();
      FlashAnimationManager.rotationListenerCleanups.delete(annotationSubject);
    }

    // Remove animation annotations from both viewers
    const removeAnimationAnnots = (wrapper: WebViewerWrapper | null) => {
      if (!wrapper) return;
      const annots = wrapper.annotationManager.getAnnotationsList().filter((a) => a.Subject === annotationSubject);
      wrapper.annotationManager.deleteAnnotations(annots, { imported: true, force: true });
    };

    removeAnimationAnnots(sourceWrapper);
    removeAnimationAnnots(targetWrapper);
  }

  /**
   * Convenience helper to stop BOTH graphics and markup animations and clean up their annotations
   */
  static stopAllAnimations(sourceWrapper: WebViewerWrapper | null, targetWrapper: WebViewerWrapper | null): void {
    // Stop graphics animation (flashing)
    FlashAnimationManager.stopAnimation(
      'graphicAnimationInterval',
      'GraphicAnnotationAnimated',
      sourceWrapper,
      targetWrapper,
    );

    // Stop graphics overlay annotations
    FlashAnimationManager.stopAnimation(
      'graphicAnimationInterval',
      'GraphicAnnotationOverlay',
      sourceWrapper,
      targetWrapper,
    );

    // Stop markup animation
    FlashAnimationManager.stopAnimation(
      'markupAnimationInterval',
      'MarkupAnnotationAnimated',
      sourceWrapper,
      targetWrapper,
    );

    // Reset animation tracking variables
    GraphicZone.animatedZone = '';
    GraphicZone.activeAnimationMode = '';
    MarkupZone.animatedMarkup = null;

    // Remove any 'loader' annotations and unhide hidden repeat annotations
    if (targetWrapper) {
      const targetAnnots = targetWrapper.getAnnotationList();
      if (Array.isArray(targetAnnots) && targetAnnots.length > 0) {
        const loaderAnnots = targetAnnots.filter((annot) => annot?.Id === 'loader');
        if (loaderAnnots.length) {
          targetWrapper.annotationManager.deleteAnnotations(loaderAnnots, { imported: false, force: true });
        }

        const hiddenRepeats = targetAnnots.filter(
          (annot) => typeof annot?.Id === 'string' && annot.Id.startsWith('repeat-') && annot.Hidden,
        );
        if (hiddenRepeats.length) {
          hiddenRepeats.forEach((annot) => {
            annot.Hidden = false;
            targetWrapper.annotationManager.updateAnnotation(annot);
          });
        }
      }
    }
  }
}
